// RecoveryForecastCard — Feature 2. A daily "weather report" for the
// Today screen: one honest, glanceable read of whether today is likely
// tougher, steady, or lighter, the reasons behind it, and the right
// coping tools pre-staged. Data from GET /api/insights/recovery-forecast.
//
// Secondary surface: stays silent while loading or on error, and hides
// itself entirely until there is enough logged data to forecast honestly.

import { useNavigate } from 'react-router-dom';
import { CloudRain, CloudSun, Sun } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { useApi } from '../../hooks/useApi';
import type { RecoveryForecast, RecoveryForecastBand } from '@shared/types';

const BAND_ICON: Record<RecoveryForecastBand, typeof Sun> = {
  tougher: CloudRain,
  steady: CloudSun,
  lighter: Sun,
};

export function RecoveryForecastCard() {
  const navigate = useNavigate();
  const res = useApi<RecoveryForecast>('/api/insights/recovery-forecast');

  // Quiet on load/error; hidden until there's enough data to be honest.
  if (res.status !== 'ok' || res.data.lowData) return null;
  const f = res.data;

  return (
    <Card accentLeft>
      <div className="row" style={{ gap: 'var(--space-3)', alignItems: 'flex-start' }}>
        <Icon icon={BAND_ICON[f.band]} size={22} />
        <div style={{ flex: 1 }}>
          <h3 className="text-h3" style={{ margin: 0 }}>
            {f.headline}
          </h3>
          {f.reasons.length > 0 ? (
            <ul
              className="muted text-caption"
              style={{ margin: 'var(--space-2) 0 0', paddingInlineStart: 'var(--space-4)' }}
            >
              {f.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          ) : null}
          {f.dangerWindow ? (
            <p
              className="muted text-caption"
              style={{ marginBlockStart: 'var(--space-2)', marginBlockEnd: 0 }}
            >
              Heads up — {f.dangerWindow.label} has been a harder window for you.
            </p>
          ) : null}
          {f.tools.length > 0 ? (
            <div
              className="row"
              style={{
                gap: 'var(--space-2)',
                flexWrap: 'wrap',
                marginBlockStart: 'var(--space-3)',
              }}
            >
              {f.tools.map((t) => (
                <Button key={t.kind} variant="tertiary" onClick={() => navigate(t.route)}>
                  {t.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
